// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useGetAllInstrumentConfigurations } from '@hooks/queries/useGetAllInstrumentConfigurations';
import { Decimal } from 'decimal.js';
import { AllocationRequest } from '@daml.js/splice-api-token-allocation-request/lib/Splice/Api/Token/AllocationRequestV1';
import { Holding as TokenStandardHolding } from '@daml.js/splice-api-token-holding/lib/Splice/Api/Token/HoldingV1';
import { useModuleParty } from '../../../contexts/ModuleContext';
import useAllocationRequests from '../../../hooks/queries/registry/allocationRequest/useAllocationRequest';
import useAllocations from '../../../hooks/queries/registry/tokenStandard/useAllocations';
import useTokenStandardHoldings from '../../../hooks/queries/registry/tokenStandard/useTokenStandardHoldings';
import { getAllocationRequestsWithConfig } from '../../../utils/getAllocationRequestsWithConfig';
import { getHoldingsToCoverAmount } from '../../../utils/getHoldingsToCoverAmount';
import { groupAllocations } from '../../../utils/groupAllocations';
import { groupHoldings } from '../../../utils/groupHoldings';
import { getInstConfigMap } from '../../../utils/instConfigsByOperator';
import { IAllocationRequest } from './types';

export const useGetAllocationRequestRows = () => {
  const party = useModuleParty();

  const allocationRequests = useAllocationRequests();
  const holdings = useTokenStandardHoldings();
  const allocations = useAllocations();

  const availableHoldings = holdings.data?.filter(
    (holding) =>
      holding.view(TokenStandardHolding).owner === party &&
      !holding.view(TokenStandardHolding).lock,
  );
  const availableHoldingsByInstrumentId = groupHoldings(availableHoldings || []);
  const allocationByIds = groupAllocations(allocations.data || []);
  const isLoading =
    allocationRequests.isLoading ||
    !allocationRequests.data ||
    holdings.isLoading ||
    !holdings.data ||
    allocations.isLoading ||
    !allocations.data;
  const { data } = useGetAllInstrumentConfigurations();

  const instrumentConfigMap = getInstConfigMap(data);

  const rows: IAllocationRequest[] | undefined = allocationRequests.data
    ?.flatMap((allocationRequest) => {
      const view = allocationRequest.view(AllocationRequest);
      return Object.entries(view.transferLegs).map(([id, transferLeg]) => {
        const inputHoldings = getHoldingsToCoverAmount(
          availableHoldingsByInstrumentId?.[transferLeg.instrumentId.admin]?.[
            transferLeg.instrumentId.id
          ] || [],
          new Decimal(transferLeg.amount),
        );
        return {
          contractId: allocationRequest.contractId as string,
          transferId: id,
          state: 'Not Allocated',
          payload: {
            settlement: view.settlement,
            transferLeg,
          },
          allocation:
            allocationByIds[view.settlement.settlementRef.id]?.[
              view.settlement.settlementRef.cid?.toString() || ''
            ]?.[id],
          inputHoldings,
        };
      });
    })
    .filter((row) => row.payload.transferLeg.sender === party);

  const rowsWithConfiguration = getAllocationRequestsWithConfig(instrumentConfigMap, rows);

  return { rows: rowsWithConfiguration, isLoading };
};
